import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import useFacilityFeatures from '../../hooks/useFacilityFeatures';
import { 
  Pill, Plus, Search, Calendar, AlertTriangle, 
  CheckCircle, Clock, Eye, Target,
  FileText, Bell, Activity as ActivityIcon,
  QrCode, Database, Lock,
  Brain as BrainIcon,
  Sparkles, TrendingUp, Package, AlertCircle, Cpu,
  Shield, Award, X, Save, Edit3, Pause, Play, XCircle
} from 'lucide-react';
import MedicationService from '../../services/medication/MedicationService';
import PharmacyOrderService from '../../services/api/PharmacyOrderService';
import { ORDER_STATUS as PHARM_ORDER_STATUS } from '../../data/models/PharmacyOrder';
import QRCodeScanner from './QRCodeScanner';
import BarcodeScanner from './BarcodeScanner';
import StockManagement from './StockManagement';
import BulkOrderingWizard from './BulkOrderingWizard';
import MyHealthRecord from './MyHealthRecord';
import PBSEligibility from './PBSEligibility';
import ENRMCCompliance from './eNRMCCompliance';
import MARForm from './MARForm';
import MARDashboard from './MARDashboard';
import { getFilteredResidents } from '../../utils/helpers';
import S8RegisterService from '../../services/api/S8RegisterService';
import AdverseDrugReactionService from '../../services/api/AdverseDrugReactionService';
import { S8_TRANSACTION_TYPE, S8_TRANSACTION_LABELS } from '../../data/models/S8Register';
import {
  ADR_SEVERITY, ADR_SEVERITY_LABELS, ADR_SEVERITY_COLOURS,
  ADR_STATUS, ADR_ACTION_LABELS,
  ADR_REPORTED_TO_LABELS,
} from '../../data/models/AdverseDrugReaction';
import { useToast } from '../Common/ToastProvider';
import { useConfirm } from '../Common/ConfirmDialog';

const Medication = ({ residentId }) => {
  const { state } = useAppContext();
  const { currentUser, deviceInfo } = state;
  const { optimization } = deviceInfo;
  const { showToast } = useToast();
  const confirm = useConfirm();
  
  // Get facility features for conditional UI
  const { isNDIS, isAgedCare } = useFacilityFeatures();

  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showEPrescribe, setShowEPrescribe] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showStockManagement, setShowStockManagement] = useState(false);
  const [showBulkOrdering, setShowBulkOrdering] = useState(false);
  const [showMyHealthRecord, setShowMyHealthRecord] = useState(false);
  const [showPBSEligibility, setShowPBSEligibility] = useState(false);
  const [showeNRMCCompliance, setShoweNRMCCompliance] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const medService = MedicationService;

  const allResidents = getFilteredResidents('all', null, '', currentUser);
  const currentResident = residentId ? allResidents.find(r => r.id === residentId) : null;
  const residentsToShow = residentId ? [currentResident].filter(Boolean) : allResidents;

  const [medicationOrders, setMedicationOrders] = useState([]);
  const [pharmacyOrders, setPharmacyOrders] = useState([]);

  // Controlled state for Add Medication modal
  const [addMedForm, setAddMedForm] = useState({
    residentId: '', residentName: '', drugName: '', dosage: '',
    route: 'oral', instructions: '', startDate: new Date().toISOString().split('T')[0],
    frequency: 'once', priority: 'routine', orderType: 'long_term',
  });

  // ePrescription form state
  const [showEPrescribeForm, setShowEPrescribeForm] = useState(false);
  const [ePrescribeForm, setEPrescribeForm] = useState({
    residentName: '', room: '', medication: '', dosage: '',
    instructions: '', startDate: new Date().toISOString().split('T')[0],
    duration: '30', priority: 'routine', pharmacy: '',
  });

  // Edit medication state
  const [editingOrder, setEditingOrder] = useState(null);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);

  // S8 Register state
  const [s8Entries, setS8Entries] = useState([]);
  const [s8Summaries, setS8Summaries] = useState([]);
  const [showS8Form, setShowS8Form] = useState(false);
  const [s8FormData, setS8FormData] = useState({
    drugName: '', genericName: '', strength: '', form: '',
    transactionType: S8_TRANSACTION_TYPE.ADMINISTERED,
    quantity: '', residentId: '', residentName: '',
    performedByName: '', witnessedByName: '',
    batchNumber: '', storageLocation: '', notes: '',
  });

  // ADR state
  const [adrRecords, setAdrRecords] = useState([]);
  const [adrStats, setAdrStats] = useState(null);
  const [showAdrForm, setShowAdrForm] = useState(false);
  const [adrFormData, setAdrFormData] = useState({
    residentId: '', residentName: '', drug: '', drugGenericName: '', drugDose: '',
    reaction: '', reactionDetails: '', severity: ADR_SEVERITY.MILD,
    actionsTaken: [], actionNotes: '', reportedTo: [],
    dateIdentified: new Date().toISOString().split('T')[0],
  });

  const loadS8Data = useCallback(async () => {
    const [entriesRes, summaryRes] = await Promise.all([
      S8RegisterService.getAll(),
      S8RegisterService.getDrugSummaries(),
    ]);
    if (entriesRes.success) setS8Entries(entriesRes.data);
    if (summaryRes.success) setS8Summaries(summaryRes.data);
  }, []);

  const loadAdrData = useCallback(async () => {
    const [allRes, statsRes] = await Promise.all([
      AdverseDrugReactionService.getAll(),
      AdverseDrugReactionService.getStats(),
    ]);
    if (allRes.success) setAdrRecords(allRes.data);
    if (statsRes.success) setAdrStats(statsRes.data);
  }, []);

  const loadMedicationOrders = useCallback(() => {
    if (residentId) {
      const result = medService.getOrdersByResident(residentId);
      if (result.success) setMedicationOrders(result.data);
    } else {
      const allOrders = [];
      const residents = getFilteredResidents('all', null, '', currentUser);
      (residents || []).forEach((r) => {
        if (r?.id) {
          const result = medService.getOrdersByResident(r.id);
          if (result.success) allOrders.push(...result.data);
        }
      });
      setMedicationOrders(allOrders);
    }
  }, [residentId, medService, currentUser]);

  const loadPharmacyOrders = useCallback(async () => {
    const result = await PharmacyOrderService.getAll(
      residentId ? { residentId } : {}
    );
    if (result.success) setPharmacyOrders(result.data);
  }, [residentId]);

  useEffect(() => {
    loadS8Data();
    loadAdrData();
    loadMedicationOrders();
    loadPharmacyOrders();
  }, [loadS8Data, loadAdrData, loadMedicationOrders, loadPharmacyOrders]);

  const handleS8Submit = async () => {
    const qty = parseInt(s8FormData.quantity, 10);
    if (isNaN(qty) || qty < 0) {
      showToast('Quantity must be a non-negative number', 'error');
      return;
    }
    const result = await S8RegisterService.recordTransaction({
      ...s8FormData,
      quantity: qty,
      performedBy: currentUser?.id || 'current_user',
      performedByName: s8FormData.performedByName || currentUser?.name || 'Current User',
      witnessedBy: `witness_${Date.now()}`,
      witnessedByName: s8FormData.witnessedByName,
    });
    if (result.success) {
      setShowS8Form(false);
      setS8FormData({
        drugName: '', genericName: '', strength: '', form: '',
        transactionType: S8_TRANSACTION_TYPE.ADMINISTERED,
        quantity: '', residentId: '', residentName: '',
        performedByName: '', witnessedByName: '',
        batchNumber: '', storageLocation: '', notes: '',
      });
      await loadS8Data();
    } else {
      showToast('Validation errors: ' + (result.errors || [result.error]).join(', '), 'error');
    }
  };

  const handleAdrSubmit = async () => {
    const result = await AdverseDrugReactionService.create({
      ...adrFormData,
      reportedBy: currentUser?.id || 'current_user',
      reportedByName: currentUser?.name || 'Current User',
    });
    if (result.success) {
      setShowAdrForm(false);
      setAdrFormData({
        residentId: '', residentName: '', drug: '', drugGenericName: '', drugDose: '',
        reaction: '', reactionDetails: '', severity: ADR_SEVERITY.MILD,
        actionsTaken: [], actionNotes: '', reportedTo: [],
        dateIdentified: new Date().toISOString().split('T')[0],
      });
      await loadAdrData();
    } else {
      showToast('Validation errors: ' + (result.errors || [result.error]).join(', '), 'error');
    }
  };

  const handleAdrResolve = async (id) => {
    const result = await AdverseDrugReactionService.resolve(id, currentUser?.id || 'current_user');
    if (result.success) await loadAdrData();
  };


  // Enhanced medication stats with comprehensive data
  const medicationStats = {
    morningRound: { count: residentId ? 1 : 15, status: 'Due Now', type: residentId ? 'Resident' : 'Residents' },
    compliance: { percentage: 98, count: residentId ? 2 : 142, type: 'Administered', timeframe: 'Today' },
    stockAlerts: { count: 5, status: 'Order required', type: 'Low stock' },
    reviewsDue: { count: residentId ? 1 : 8, urgent: residentId ? 0 : 3, timeframe: 'This week' },
    aiInsights: { count: 12, critical: 3, recommendations: 8 },
    s8Controlled: { count: 7, variance: 0, locked: 2 },
    ePrescriptions: { pending: 5, sent: 23, errors: 1 },
    totalMedications: { count: 247, active: 189, discontinued: 58 },
  };

  // AI Insights Data
  const aiInsightsData = {
    adherencePatterns: {
      overallScore: 94.2,
      trends: {
        improving: 8,
        declining: 2,
        stable: 15
      },
      riskFactors: [
        { factor: 'Polypharmacy', risk: 'High', impact: '15% adherence reduction', residents: 12 },
        { factor: 'Complex dosing', risk: 'Medium', impact: '8% adherence reduction', residents: 8 },
        { factor: 'Cognitive decline', risk: 'High', impact: '22% adherence reduction', residents: 5 },
        { factor: 'Social isolation', risk: 'Medium', impact: '6% adherence reduction', residents: 3 }
      ]
    },
    predictiveAnalytics: {
      fallRisk: { score: 23, trend: 'decreasing', confidence: 87 },
      medicationErrors: { predicted: 2, timeframe: 'next 7 days', confidence: 92 },
      hospitalReadmission: { risk: 'Low', probability: 12, confidence: 89 },
      adverseEvents: { predicted: 1, type: 'Drug interaction', confidence: 94 }
    },
    recommendations: [
      {
        id: 'REC001',
        type: 'Adherence',
        priority: 'High',
        title: 'Implement medication reminder system for John Smith',
        description: 'AI detected 23% missed doses in the last 2 weeks. Recommend automated reminders.',
        impact: 'Expected 15% improvement in adherence',
        residents: ['John Smith'],
        status: 'Pending',
        aiConfidence: 94
      },
      {
        id: 'REC002',
        type: 'Safety',
        priority: 'Critical',
        title: 'Review Warfarin dosing for Margaret Johnson',
        description: 'AI detected potential drug interaction with newly prescribed antibiotic.',
        impact: 'Prevent potential bleeding risk',
        residents: ['Margaret Johnson'],
        status: 'Urgent',
        aiConfidence: 98
      },
      {
        id: 'REC003',
        type: 'Optimization',
        priority: 'Medium',
        title: 'Consolidate medication times for Eleanor Davis',
        description: 'AI suggests consolidating 3 separate medication times into 2 to improve adherence.',
        impact: 'Expected 8% improvement in compliance',
        residents: ['Eleanor Davis'],
        status: 'Recommended',
        aiConfidence: 87
      }
    ],
    riskAssessment: {
      highRisk: 3,
      mediumRisk: 7,
      lowRisk: 12,
      totalResidents: 22,
      riskFactors: [
        { factor: 'Multiple medications (8+)', count: 5, risk: 'High' },
        { factor: 'Recent medication changes', count: 8, risk: 'Medium' },
        { factor: 'Cognitive impairment', count: 3, risk: 'High' },
        { factor: 'History of non-adherence', count: 4, risk: 'Medium' }
      ]
    }
  };


  // Enhanced ePrescribe data
  const ePrescriptions = [
    {
      id: 'EP001',
      resident: 'Margaret Johnson',
      medication: 'Metformin 500mg',
      doctor: 'Dr. Sarah Mitchell',
      status: 'Pending',
      created: '2024-01-20',
      expires: '2024-01-27',
      dosage: '500mg twice daily',
      indication: 'Type 2 Diabetes',
      pharmacy: 'Sunshine Pharmacy',
      priority: 'Standard',
      interactions: ['Warfarin - Monitor INR'],
      allergies: 'None known'
    },
    {
      id: 'EP002',
      resident: 'Robert Wilson',
      medication: 'Insulin Glargine',
      doctor: 'Dr. James Brown',
      status: 'Sent to Pharmacy',
      created: '2024-01-19',
      expires: '2024-01-26',
      dosage: '20 units daily',
      indication: 'Type 1 Diabetes',
      pharmacy: 'Bayview Pharmacy',
      priority: 'High',
      interactions: 'None',
      allergies: 'None known'
    },
    {
      id: 'EP003',
      resident: 'Elizabeth Brown',
      medication: 'Lisinopril 10mg',
      doctor: 'Dr. Sarah Mitchell',
      status: 'Approved',
      created: '2024-01-18',
      expires: '2024-01-25',
      dosage: '10mg once daily',
      indication: 'Hypertension',
      pharmacy: 'Community Pharmacy',
      priority: 'Standard',
      interactions: 'None',
      allergies: 'None known'
    },
    {
      id: 'EP004',
      resident: 'James Mitchell',
      medication: 'Atorvastatin 20mg',
      doctor: 'Dr. James Brown',
      status: 'Error',
      created: '2024-01-20',
      expires: '2024-01-27',
      dosage: '20mg once daily',
      indication: 'Hyperlipidemia',
      pharmacy: 'Sunshine Pharmacy',
      priority: 'Standard',
      interactions: 'None',
      allergies: 'None known',
      errorReason: 'Pharmacy system unavailable'
    }
  ];

  // Generate medication data based on context
  const generateMedicationData = () => {
    if (residentId && currentResident) {
      // Resident-specific medications
      return currentResident.prescriptions?.map((med, index) => ({
        id: `MR${residentId}${index}`,
        residentName: currentResident.name,
        room: currentResident.room,
        medicationsDue: 1,
        medications: [{
          name: med.medication,
          icon: '💊',
          instructions: med.dosage,
          status: 'pending'
        }],
        avatar: currentResident.avatar || '👵'
      })) || [];
    } else {
      // All residents' medications for dashboard
      if (residentsToShow.length === 0) {
        // Fallback data if no residents are available
        return [
          {
            id: 'MR001',
            residentName: 'Margaret Johnson',
            room: 'Room 101',
            medicationsDue: 3,
            status: 'In Progress',
            time: '08:00',
            nurse: 'Nurse Johnson',
            priority: 'High',
            notes: 'Monitor blood pressure',
            avatar: '👵'
          },
          {
            id: 'MR002',
            residentName: 'Robert Wilson',
            room: 'Room 102',
            medicationsDue: 2,
            status: 'Pending',
            time: '08:30',
            nurse: 'Nurse Williams',
            priority: 'Medium',
            notes: 'Check blood sugar',
            avatar: '👴'
          },
          {
            id: 'MR003',
            residentName: 'Elizabeth Brown',
            room: 'Room 103',
            medicationsDue: 4,
            status: 'Completed',
            time: '09:00',
            nurse: 'Nurse Smith',
            priority: 'Low',
            notes: 'All medications administered',
            avatar: '👵'
          }
        ];
      }
      
      return residentsToShow.slice(0, 10).map((resident, index) => ({
        id: `MR${resident?.id || index}${index}`,
        residentName: resident?.name || 'Unknown Resident',
        room: resident?.room || 'Room Unknown',
        medicationsDue: resident?.prescriptions?.length || Math.floor(Math.random() * 5) + 1,
        status: index % 3 === 0 ? 'In Progress' : index % 3 === 1 ? 'Pending' : 'Completed',
        time: index % 2 === 0 ? '08:00' : '14:00',
        nurse: index % 2 === 0 ? 'Nurse Johnson' : 'Nurse Williams',
        priority: index % 4 === 0 ? 'High' : index % 4 === 1 ? 'Medium' : 'Low',
        notes: index % 3 === 0 ? 'Monitor blood pressure' : '',
        avatar: resident?.avatar || '👵'
      }));
    }
  };

  const medicationRounds = generateMedicationData() || [];
  


  // Button functionality handlers
  const showNotification = (message, type = 'success') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date().toLocaleTimeString()
    };
    setNotifications(prev => [...prev, notification]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  const handleNewMedication = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showNotification('New medication form opened successfully', 'success');
      setActiveTab('eprescribe');
    }, 1000);
  };

  const handleQRScan = (scanData) => {
    console.log('QR Code scanned:', scanData);
    showNotification(`QR Code scanned for ${scanData.residentName} - ${scanData.medication}`, 'success');
  };

  const handleBarcodeScan = (scanData) => {
    console.log('Barcode scanned:', scanData);
    showNotification(`Barcode verified for ${scanData.medication}`, 'success');
  };

  const handleNewRound = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showNotification('New medication round started', 'success');
      setActiveTab('rounds');
    }, 1000);
  };

  const handleDateRange = () => {
    showNotification('Date range picker opened', 'info');
  };

  const handleStartScanning = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showNotification('QR code scanner activated', 'success');
    }, 1500);
  };

  const handleAdministerMedication = (medicationId, residentName) => {
    setLoading(true);
    const adminResident = residentsToShow.find((r) => r?.name === residentName);
    const result = medService.recordAdministration({
      medicationOrderId: medicationId || 'manual_admin',
      residentId: adminResident?.id || residentId || 'unknown',
      administeredBy: currentUser?.id || 'current_user',
      administeredByName: currentUser?.name || 'Current User',
      status: 'given',
      notes: `Administered to ${residentName}`,
    });
    setLoading(false);
    if (result.success) {
      showNotification(`Medication administered to ${residentName}`, 'success');
      loadMedicationOrders();
    } else {
      showNotification(result.error || 'Failed to record administration', 'error');
    }
  };

  const handleViewDetails = (medicationId) => {
    const order = medicationOrders.find((o) => o.id === medicationId);
    if (order) {
      setEditingOrder(order);
      setShowEditOrderModal(true);
    } else {
      showNotification('Medication details opened', 'info');
    }
  };

  const handleCompleteRound = (roundId) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showNotification('Medication round completed', 'success');
      loadMedicationOrders();
    }, 500);
  };

  const handleStartNewRound = () => {
    showNotification('New medication round initiated', 'success');
    setActiveTab('rounds');
  };

  const handleCeaseOrder = (orderId, reason) => {
    const result = medService.ceaseOrder(orderId, reason || 'Discontinued by clinician', currentUser?.id || 'system');
    if (result.success) {
      showNotification('Medication discontinued', 'success');
      loadMedicationOrders();
    } else {
      showNotification(result.error || 'Failed to discontinue', 'error');
    }
  };

  const handleHoldOrder = (orderId, reason) => {
    const result = medService.holdOrder(orderId, reason || 'Placed on hold', currentUser?.id || 'system');
    if (result.success) {
      showNotification('Medication placed on hold', 'success');
      loadMedicationOrders();
    } else {
      showNotification(result.error || 'Failed to hold', 'error');
    }
  };

  const handleResumeOrder = (orderId) => {
    const result = medService.resumeOrder(orderId, currentUser?.id || 'system');
    if (result.success) {
      showNotification('Medication reactivated', 'success');
      loadMedicationOrders();
    } else {
      showNotification(result.error || 'Failed to resume', 'error');
    }
  };

  const handleSaveEditOrder = () => {
    if (!editingOrder) return;
    const result = medService.createOrder({ ...editingOrder });
    if (result.success) {
      showNotification('Medication updated', 'success');
      setShowEditOrderModal(false);
      setEditingOrder(null);
      loadMedicationOrders();
    } else {
      showNotification(result.error || 'Failed to update', 'error');
    }
  };

  const handleAddMedicationSubmit = () => {
    const resident = residentsToShow.find(
      (r) => r?.id?.toString() === addMedForm.residentId || r?.name === addMedForm.residentName
    );
    const result = medService.createOrder({
      residentId: addMedForm.residentId || resident?.id || residentId || 'unknown',
      drugName: addMedForm.drugName,
      dose: addMedForm.dosage,
      route: addMedForm.route,
      frequency: addMedForm.frequency,
      instructions: addMedForm.instructions,
      startDate: addMedForm.startDate,
      orderType: addMedForm.orderType,
      prescriberId: currentUser?.id || 'system',
      prescriberName: currentUser?.name || 'System',
    });
    if (result.success) {
      showNotification('Medication added successfully', 'success');
      setShowEPrescribe(false);
      setAddMedForm({
        residentId: '', residentName: '', drugName: '', dosage: '',
        route: 'oral', instructions: '', startDate: new Date().toISOString().split('T')[0],
        frequency: 'once', priority: 'routine', orderType: 'long_term',
      });
      loadMedicationOrders();
    } else {
      showNotification(result.error || 'Failed to add medication', 'error');
    }
  };

  const handleEPrescribeSubmit = async () => {
    const result = await PharmacyOrderService.create({
      residentName: ePrescribeForm.residentName || currentResident?.name || '',
      residentId: residentId || '',
      ward: ePrescribeForm.room || currentResident?.room || '',
      items: [{ drug: ePrescribeForm.medication, strength: ePrescribeForm.dosage, qty: 1 }],
      pharmacy: ePrescribeForm.pharmacy || 'Default Pharmacy',
      notes: ePrescribeForm.instructions,
      priority: ePrescribeForm.priority === 'urgent' ? 'Urgent' : 'Medium',
    }, currentUser?.id || 'system');
    if (result.success) {
      showNotification('ePrescription created and sent to pharmacy', 'success');
      setShowEPrescribe(false);
      setEPrescribeForm({
        residentName: '', room: '', medication: '', dosage: '',
        instructions: '', startDate: new Date().toISOString().split('T')[0],
        duration: '30', priority: 'routine', pharmacy: '',
      });
      loadPharmacyOrders();
    } else {
      showNotification(result.error || 'Failed to create ePrescription', 'error');
    }
  };

  // Enhanced medication schedule data with comprehensive details
  const medicationSchedule = [
    {
      id: 'MS001',
      time: '08:00',
      round: 'Morning',
      status: 'In Progress',
      residents: residentId ? 1 : 15,
      completed: residentId ? 1 : 12,
      remaining: residentId ? 0 : 3,
      nurse: 'Sarah Johnson',
      notes: 'All medications administered on time',
      medications: ['Metformin', 'Lisinopril', 'Atorvastatin', 'Insulin'],
      duration: '45 minutes',
      efficiency: '94%',
      issues: 0,
      criticalMeds: 2
    },
    {
      id: 'MS002',
      time: '12:00',
      round: 'Noon',
      status: 'Scheduled',
      residents: residentId ? 1 : 12,
      completed: 0,
      remaining: residentId ? 1 : 12,
      nurse: 'Mike Wilson',
      notes: 'Lunch medications due',
      medications: ['Warfarin', 'Digoxin', 'Furosemide'],
      duration: '30 minutes',
      efficiency: 'N/A',
      issues: 0,
      criticalMeds: 1
    },
    {
      id: 'MS003',
      time: '18:00',
      round: 'Evening',
      status: 'Scheduled',
      residents: residentId ? 1 : 18,
      completed: 0,
      remaining: residentId ? 1 : 18,
      nurse: 'Lisa Brown',
      notes: 'Dinner medications due',
      medications: ['Omeprazole', 'Tramadol', 'Melatonin'],
      duration: '35 minutes',
      efficiency: 'N/A',
      issues: 0,
      criticalMeds: 0
    },
    {
      id: 'MS004',
      time: '22:00',
      round: 'Night',
      status: 'Scheduled',
      residents: residentId ? 1 : 8,
      completed: 0,
      remaining: residentId ? 1 : 8,
      nurse: 'Emma Davis',
      notes: 'Bedtime medications due',
      medications: ['Lorazepam', 'Temazepam', 'Morphine'],
      duration: '25 minutes',
      efficiency: 'N/A',
      issues: 0,
      criticalMeds: 3
    }
  ];

  // Enhanced medication history data
  const medicationHistory = [
    {
      id: 'MH001',
      date: '2024-01-20',
      time: '08:15',
      residentName: residentId ? currentResident?.name || 'Resident' : 'Margaret Johnson',
      medication: 'Metformin 500mg',
      action: 'Administered',
      nurse: 'Sarah Johnson',
      notes: 'Taken with breakfast, no side effects',
      status: 'completed'
    },
    {
      id: 'MH002',
      date: '2024-01-20',
      time: '08:30',
      residentName: residentId ? currentResident?.name || 'Resident' : 'Robert Wilson',
      medication: 'Insulin 10 units',
      action: 'Administered',
      nurse: 'Sarah Johnson',
      notes: 'Blood glucose 120 mg/dL before administration',
      status: 'completed'
    },
    {
      id: 'MH003',
      date: '2024-01-20',
      time: '12:15',
      residentName: residentId ? currentResident?.name || 'Resident' : 'Elizabeth Brown',
      medication: 'Lisinopril 10mg',
      action: 'Administered',
      nurse: 'Mike Wilson',
      notes: 'Blood pressure 135/85 before administration',
      status: 'completed'
    },
    {
      id: 'MH004',
      date: '2024-01-20',
      time: '18:20',
      residentName: residentId ? currentResident?.name || 'Resident' : 'James Mitchell',
      medication: 'Albuterol inhaler',
      action: 'Administered',
      nurse: 'Lisa Brown',
      notes: 'Used before dinner, breathing improved',
      status: 'completed'
    },
    {
      id: 'MH005',
      date: '2024-01-20',
      time: '22:10',
      residentName: residentId ? currentResident?.name || 'Resident' : 'Dorothy Williams',
      medication: 'Melatonin 3mg',
      action: 'Administered',
      nurse: 'Emma Davis',
      notes: 'Given 30 minutes before bedtime',
      status: 'completed'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'administered': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // const getPriorityColor = (priority) => {
  //   switch (priority) {
  //     case 'Critical': return 'bg-red-100 text-red-800';
  //     case 'High': return 'bg-orange-100 text-orange-800';
  //     case 'Medium': return 'bg-yellow-100 text-yellow-800';
  //     case 'Low': return 'bg-green-100 text-green-800';
  //     default: return 'bg-gray-100 text-gray-800';
  //   }
  // };

  const filteredRounds = (medicationRounds || []).filter(round => {
    if (!round) return false;
    const matchesSearch = (round.residentName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (round.room || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Notification System */}
      {notifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
                notification.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
                notification.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
                notification.type === 'info' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                'bg-gray-100 text-gray-800 border border-gray-200'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${
                notification.type === 'success' ? 'bg-green-500' :
                notification.type === 'error' ? 'bg-red-500' :
                notification.type === 'info' ? 'bg-blue-500' :
                'bg-gray-500'
              }`}></div>
              <span className="text-sm font-medium">{notification.message}</span>
              <span className="text-xs opacity-75">{notification.timestamp}</span>
            </div>
          ))}
        </div>
      )}

      {/* Add Medication Modal */}
      {showEPrescribe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-3">
                <Pill className="w-6 h-6 text-blue-600" />
                Add New Medication
              </h2>
              <button onClick={() => setShowEPrescribe(false)} className="text-gray-500 hover:text-gray-700"><X className="w-6 h-6" /></button>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resident</label>
                  <select value={addMedForm.residentId} onChange={(e) => setAddMedForm((p) => ({ ...p, residentId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="">Select resident...</option>
                    {residentsToShow.map(resident => (
                      <option key={resident.id} value={resident.id}>{resident.name} - {resident.room}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Medication Name</label>
                  <input type="text" value={addMedForm.drugName} onChange={(e) => setAddMedForm((p) => ({ ...p, drugName: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Metformin 500mg" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dosage</label>
                  <input type="text" value={addMedForm.dosage} onChange={(e) => setAddMedForm((p) => ({ ...p, dosage: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., 500mg twice daily" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Route</label>
                  <select value={addMedForm.route} onChange={(e) => setAddMedForm((p) => ({ ...p, route: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="oral">Oral</option>
                    <option value="injection">Injection</option>
                    <option value="topical">Topical</option>
                    <option value="inhalation">Inhalation</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
                <textarea value={addMedForm.instructions} onChange={(e) => setAddMedForm((p) => ({ ...p, instructions: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24" placeholder="Enter detailed medication instructions..." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input type="date" value={addMedForm.startDate} onChange={(e) => setAddMedForm((p) => ({ ...p, startDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                  <select value={addMedForm.frequency} onChange={(e) => setAddMedForm((p) => ({ ...p, frequency: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="once">Once daily</option>
                    <option value="twice">Twice daily</option>
                    <option value="three">Three times daily</option>
                    <option value="four">Four times daily</option>
                    <option value="as-needed">As needed (PRN)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
                  <select value={addMedForm.orderType} onChange={(e) => setAddMedForm((p) => ({ ...p, orderType: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="long_term">Long Term</option>
                    <option value="short_term">Short Term</option>
                    <option value="stat">STAT</option>
                    <option value="prn">PRN</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button onClick={() => setShowEPrescribe(false)} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                <button onClick={handleAddMedicationSubmit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"><Save className="w-4 h-4" /> Add Medication</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Medication Order Modal */}
      {showEditOrderModal && editingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-blue-800 flex items-center gap-2"><Edit3 className="w-5 h-5" /> Edit Medication Order</h2>
              <button onClick={() => { setShowEditOrderModal(false); setEditingOrder(null); }} className="text-gray-500 hover:text-gray-700"><X className="w-6 h-6" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Drug Name</label>
                <input type="text" value={editingOrder.drugName || ''} onChange={(e) => setEditingOrder((p) => ({ ...p, drugName: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dose</label>
                <input type="text" value={editingOrder.dose || ''} onChange={(e) => setEditingOrder((p) => ({ ...p, dose: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Route</label>
                <select value={editingOrder.route || 'oral'} onChange={(e) => setEditingOrder((p) => ({ ...p, route: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="oral">Oral</option>
                  <option value="injection">Injection</option>
                  <option value="topical">Topical</option>
                  <option value="inhalation">Inhalation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <input type="text" value={editingOrder.frequency || ''} onChange={(e) => setEditingOrder((p) => ({ ...p, frequency: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                <textarea value={editingOrder.instructions || ''} onChange={(e) => setEditingOrder((p) => ({ ...p, instructions: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`px-3 py-1 text-sm rounded-full font-medium ${editingOrder.status === 'active' ? 'bg-green-100 text-green-800' : editingOrder.status === 'on_hold' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{editingOrder.status}</span>
              </div>
            </div>
            <div className="flex justify-between mt-6 pt-4 border-t">
              <div className="flex gap-2">
                {editingOrder.status === 'active' && (
                  <>
                    <button onClick={() => { handleHoldOrder(editingOrder.id, 'Placed on hold via edit'); setShowEditOrderModal(false); }} className="px-3 py-2 text-yellow-700 bg-yellow-100 rounded-lg hover:bg-yellow-200 flex items-center gap-1 text-sm"><Pause className="w-4 h-4" /> Hold</button>
                    <button onClick={() => { handleCeaseOrder(editingOrder.id, 'Ceased via edit'); setShowEditOrderModal(false); }} className="px-3 py-2 text-red-700 bg-red-100 rounded-lg hover:bg-red-200 flex items-center gap-1 text-sm"><XCircle className="w-4 h-4" /> Discontinue</button>
                  </>
                )}
                {editingOrder.status === 'on_hold' && (
                  <button onClick={() => { handleResumeOrder(editingOrder.id); setShowEditOrderModal(false); }} className="px-3 py-2 text-green-700 bg-green-100 rounded-lg hover:bg-green-200 flex items-center gap-1 text-sm"><Play className="w-4 h-4" /> Reactivate</button>
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowEditOrderModal(false); setEditingOrder(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleSaveEditOrder} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"><Save className="w-4 h-4" /> Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ePrescription Form Modal */}
      {showEPrescribeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-blue-800 flex items-center gap-2"><FileText className="w-5 h-5" /> New ePrescription</h2>
              <button onClick={() => setShowEPrescribeForm(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Patient Name</label><input type="text" value={ePrescribeForm.residentName} onChange={(e) => setEPrescribeForm(p => ({...p, residentName: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="Resident name" /></div>
                <div><label className="block text-sm font-medium mb-1">Room</label><input type="text" value={ePrescribeForm.room} onChange={(e) => setEPrescribeForm(p => ({...p, room: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Medication</label><input type="text" value={ePrescribeForm.medication} onChange={(e) => setEPrescribeForm(p => ({...p, medication: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Metformin 500mg" /></div>
                <div><label className="block text-sm font-medium mb-1">Dosage</label><input type="text" value={ePrescribeForm.dosage} onChange={(e) => setEPrescribeForm(p => ({...p, dosage: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Instructions</label><textarea value={ePrescribeForm.instructions} onChange={(e) => setEPrescribeForm(p => ({...p, instructions: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Pharmacy</label><input type="text" value={ePrescribeForm.pharmacy} onChange={(e) => setEPrescribeForm(p => ({...p, pharmacy: e.target.value}))} className="w-full px-3 py-2 border rounded-lg" placeholder="Pharmacy name" /></div>
                <div><label className="block text-sm font-medium mb-1">Priority</label><select value={ePrescribeForm.priority} onChange={(e) => setEPrescribeForm(p => ({...p, priority: e.target.value}))} className="w-full px-3 py-2 border rounded-lg"><option value="routine">Routine</option><option value="urgent">Urgent</option></select></div>
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t">
                <button onClick={() => setShowEPrescribeForm(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={() => { handleEPrescribeSubmit(); setShowEPrescribeForm(false); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Send to Pharmacy</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              {residentId 
                ? `${currentResident?.name || (isNDIS ? 'Participant' : 'Resident')}'s ${isNDIS ? 'Health Support' : 'Medications'}` 
                : isNDIS ? 'Health Support Management' : 'Medication Management'}
              <Pill className="w-8 h-8 text-blue-300 animate-pulse" />
            </h1>
            <p className="text-base mt-2 opacity-90">
              {residentId 
                ? isNDIS 
                  ? 'Manage participant health support and medication schedules' 
                  : 'Manage resident medications and schedules'
                : isNDIS
                  ? 'Monitor health support administration across all participants'
                  : 'Monitor medication administration across all residents'}
            </p>
          </div>
          {/* Only show button for Aged Care or NDIS facilities */}
          {(isAgedCare || isNDIS) && (
            <button 
              onClick={residentId ? handleNewMedication : handleNewRound}
              disabled={loading}
              className={`bg-white text-blue-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Plus className={`${optimization.iconSize}`} />
              )}
              {residentId ? 'Add Medication' : 'New Round'}
            </button>
          )}
        </div>
      </div>

      {/* Enhanced Medication Stats — min-height + no overflow clip so labels stay readable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 items-stretch">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 sm:p-6 shadow-lg border border-blue-200 hover:shadow-xl transition-all duration-300 flex flex-col min-h-[168px]">
          <div className="flex items-start justify-between gap-2 mb-3 shrink-0">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shrink-0">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 text-right min-w-0">
              <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0" />
              <span className="text-xs sm:text-sm text-orange-600 font-medium leading-tight">Due Now</span>
            </div>
          </div>
          <div className="flex flex-col flex-1 justify-end gap-1 pt-1 min-h-0">
            <h3 className="text-3xl font-bold text-blue-900 tabular-nums leading-none">{medicationStats.morningRound.count}</h3>
            <p className="text-sm text-blue-800 font-semibold leading-snug">{medicationStats.morningRound.type} Due</p>
            <p className="text-xs text-blue-600 leading-snug">{medicationStats.morningRound.status}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-5 sm:p-6 shadow-lg border border-green-200 hover:shadow-xl transition-all duration-300 flex flex-col min-h-[168px]">
          <div className="flex items-start justify-between gap-2 mb-3 shrink-0">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shrink-0">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 text-right min-w-0">
              <TrendingUp className="w-4 h-4 text-green-600 shrink-0" />
              <span className="text-xs sm:text-sm text-green-600 font-medium leading-tight">+2.1%</span>
            </div>
          </div>
          <div className="flex flex-col flex-1 justify-end gap-1 pt-1 min-h-0">
            <h3 className="text-3xl font-bold text-green-900 tabular-nums leading-none">{medicationStats.compliance.percentage}%</h3>
            <p className="text-sm text-green-800 font-semibold leading-snug">Compliance Rate</p>
            <p className="text-xs text-green-600 leading-snug">Excellent performance</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-5 sm:p-6 shadow-lg border border-orange-200 hover:shadow-xl transition-all duration-300 flex flex-col min-h-[168px]">
          <div className="flex items-start justify-between gap-2 mb-3 shrink-0">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shrink-0">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 text-right min-w-0">
              <AlertCircle className="w-4 h-4 text-orange-600 shrink-0" />
              <span className="text-xs sm:text-sm text-orange-600 font-medium leading-tight">Action Required</span>
            </div>
          </div>
          <div className="flex flex-col flex-1 justify-end gap-1 pt-1 min-h-0">
            <h3 className="text-3xl font-bold text-orange-900 tabular-nums leading-none">{medicationStats.stockAlerts.count}</h3>
            <p className="text-sm text-orange-800 font-semibold leading-snug">Stock Alerts</p>
            <p className="text-xs text-orange-600 leading-snug">{medicationStats.stockAlerts.status}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 sm:p-6 shadow-lg border border-purple-200 hover:shadow-xl transition-all duration-300 flex flex-col min-h-[168px]">
          <div className="flex items-start justify-between gap-2 mb-3 shrink-0">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shrink-0">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 text-right min-w-0">
              <Clock className="w-4 h-4 text-purple-600 shrink-0" />
              <span className="text-xs sm:text-sm text-purple-600 font-medium leading-tight">{medicationStats.reviewsDue.timeframe}</span>
            </div>
          </div>
          <div className="flex flex-col flex-1 justify-end gap-1 pt-1 min-h-0">
            <h3 className="text-3xl font-bold text-purple-900 tabular-nums leading-none">{medicationStats.reviewsDue.count}</h3>
            <p className="text-sm text-purple-800 font-semibold leading-snug">Reviews Due</p>
            <p className="text-xs text-purple-600 leading-snug">Schedule review needed</p>
          </div>
        </div>
      </div>

      {/* Enhanced Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-t-2xl">
          <nav className="flex space-x-8 px-6 py-2">
            {[
              { id: 'overview', label: '📊 Overview', icon: ActivityIcon },
              { id: 'rounds', label: '💊 Medication Rounds', icon: Clock },
              { id: 'eprescribe', label: '📝 ePrescribe', icon: FileText },
              { id: 'mar', label: '📋 MAR/eMAR', icon: Database },
              { id: 's8', label: '🔒 S8 Register', icon: Lock },
              { id: 'adr', label: '⚠️ ADR', icon: AlertTriangle },
              { id: 'ai', label: '🤖 AI Insights', icon: BrainIcon },
              { id: 'schedule', label: '📅 Schedule', icon: Calendar },
              { id: 'history', label: '📋 History', icon: FileText }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50 rounded-t-lg'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 rounded-t-lg'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Enhanced Search and Filters */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={residentId ? "Search medications..." : "Search residents, medications..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                />
              </div>
              <div className="flex gap-3">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm font-medium"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="administered">Administered</option>
                  <option value="overdue">Overdue</option>
                  <option value="scheduled">Scheduled</option>
                </select>
                <button 
                  onClick={handleDateRange}
                  className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 bg-white shadow-sm font-medium transition-all duration-300"
                >
                  <Calendar className="w-4 h-4" />
                  Date Range
                </button>
              </div>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'eprescribe' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-blue-800 flex items-center gap-3">
                    <FileText className="w-6 h-6 text-blue-600" />
                    {isNDIS ? 'Health Support Prescribing (ePrescribe)' : 'Electronic Prescribing (ePrescribe)'}
                  </h3>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setShowEPrescribeForm(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      New ePrescription
                    </button>
                    <button 
                      onClick={() => setShowMyHealthRecord(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      My Health Record
                    </button>
                    <button 
                      onClick={() => setShowPBSEligibility(true)}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      <Shield className="w-4 h-4" />
                      PBS Eligibility
                    </button>
                    <button 
                      onClick={() => setShoweNRMCCompliance(true)}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    >
                      <Award className="w-4 h-4" />
                      eNRMC Compliance
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="text-sm text-gray-600">Pending</p>
                        <p className="text-2xl font-bold text-orange-600">{medicationStats.ePrescriptions.pending}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm text-gray-600">Sent to Pharmacy</p>
                        <p className="text-2xl font-bold text-green-600">{medicationStats.ePrescriptions.sent}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="text-sm text-gray-600">Errors</p>
                        <p className="text-2xl font-bold text-red-600">{medicationStats.ePrescriptions.errors}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {(pharmacyOrders.length > 0 ? pharmacyOrders : ePrescriptions).map((prescription) => {
                    const isLive = !!prescription.items;
                    const displayName = isLive ? prescription.residentName : prescription.resident;
                    const displayMed = isLive ? (prescription.items?.[0]?.drug || 'N/A') : prescription.medication;
                    const displayStatus = prescription.status || 'Pending';
                    return (
                      <div key={prescription.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{displayName}</h4>
                            <p className="text-sm text-gray-600">{displayMed}</p>
                            <p className="text-xs text-gray-500">{prescription.prescriberName ? `Dr. ${prescription.prescriberName}` : ''} {prescription.createdAt ? `• ${new Date(prescription.createdAt).toLocaleDateString()}` : ''}</p>
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                              displayStatus.includes('Pending') ? 'bg-orange-100 text-orange-800' :
                              displayStatus.includes('Sent') || displayStatus.includes('Accepted') ? 'bg-green-100 text-green-800' :
                              displayStatus.includes('Cancelled') ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>{displayStatus}</span>
                          </div>
                        </div>
                        {isLive && (
                          <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                            {displayStatus === PHARM_ORDER_STATUS.PENDING_APPROVAL && (
                              <button onClick={async () => { await PharmacyOrderService.updateStatus(prescription.id, PHARM_ORDER_STATUS.SENT, currentUser?.id); loadPharmacyOrders(); showNotification('Sent to pharmacy', 'success'); }} className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200">Send to Pharmacy</button>
                            )}
                            {displayStatus === PHARM_ORDER_STATUS.SENT && (
                              <button onClick={async () => { await PharmacyOrderService.updateStatus(prescription.id, PHARM_ORDER_STATUS.COMPLETED, currentUser?.id); loadPharmacyOrders(); showNotification('Marked as completed', 'success'); }} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">Mark Completed</button>
                            )}
                            {displayStatus !== PHARM_ORDER_STATUS.CANCELLED && displayStatus !== PHARM_ORDER_STATUS.COMPLETED && (
                              <button onClick={async () => { await PharmacyOrderService.cancel(prescription.id, currentUser?.id); loadPharmacyOrders(); showNotification('Order cancelled', 'success'); }} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">Cancel</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mar' && (
            <MARDashboard 
              participantId={residentId}
              medication={null}
            />
          )}

          {activeTab === 's8' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-xl p-6 border border-red-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-red-800 flex items-center gap-3">
                    <Lock className="w-6 h-6 text-red-600" />
                    {isNDIS ? 'S8 Controlled Substance Register (NDIS)' : 'S8 Controlled Drug Register'}
                  </h3>
                  <button
                    onClick={() => setShowS8Form(true)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Entry
                  </button>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="text-sm text-gray-600">Unique S8 Drugs</p>
                        <p className="text-2xl font-bold text-red-600">{s8Summaries.length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <div className="flex items-center gap-3">
                      <Database className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-600">Total Transactions</p>
                        <p className="text-2xl font-bold text-blue-600">{s8Entries.length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm text-gray-600">Variance</p>
                        <p className="text-2xl font-bold text-green-600">{s8Summaries.filter(s => s.hasVariance).length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="text-sm text-gray-600">Total On Hand</p>
                        <p className="text-2xl font-bold text-orange-600">{s8Summaries.reduce((a, s) => a + s.currentBalance, 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Drug summaries */}
                {s8Summaries.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-red-800 mb-3">Current Balances</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {s8Summaries.map((s) => (
                        <div key={s.id} className="bg-white p-4 rounded-lg border border-red-200 flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-gray-900">{s.drugName} {s.strength}</p>
                            <p className="text-xs text-gray-500">{s.storageLocation || 'No location set'} {s.batchNumber ? `• Batch: ${s.batchNumber}` : ''}</p>
                            {s.lastCountDate && <p className="text-xs text-gray-400">Last count: {new Date(s.lastCountDate).toLocaleDateString()}</p>}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-red-600">{s.currentBalance}</p>
                            {s.hasVariance && <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800 font-medium">Variance</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transaction log */}
                <h4 className="font-semibold text-red-800 mb-3">Transaction Register</h4>
                {s8Entries.length === 0 ? (
                  <div className="bg-white p-8 rounded-lg border border-red-200 text-center text-gray-500">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-red-300" />
                    <p className="font-medium">No S8 transactions recorded yet.</p>
                    <p className="text-sm mt-1">Click "New Entry" to record a transaction.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...s8Entries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((entry) => (
                      <div key={entry.id} className="bg-white p-4 rounded-lg border border-red-200 hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900">{entry.drugName} {entry.strength}</h4>
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                entry.transactionType === S8_TRANSACTION_TYPE.RECEIVED || entry.transactionType === S8_TRANSACTION_TYPE.TRANSFERRED_IN
                                  ? 'bg-green-100 text-green-800'
                                  : entry.transactionType === S8_TRANSACTION_TYPE.DISCREPANCY
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-blue-100 text-blue-800'
                              }`}>
                                {S8_TRANSACTION_LABELS[entry.transactionType] || entry.transactionType}
                              </span>
                            </div>
                            {entry.residentName && <p className="text-sm text-gray-600">{entry.residentName}</p>}
                            <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                              <div>
                                <p className="text-gray-500"><strong>Performed:</strong> {entry.performedByName}</p>
                                <p className="text-gray-500"><strong>Witness:</strong> {entry.witnessedByName}</p>
                              </div>
                              <div>
                                <p className="text-gray-500"><strong>Qty:</strong> {entry.quantity}</p>
                                <p className="text-gray-500"><strong>Balance:</strong> {entry.balanceBefore} → {entry.balanceAfter}</p>
                              </div>
                            </div>
                            {entry.notes && <p className="text-xs text-gray-500 mt-1 italic">{entry.notes}</p>}
                          </div>
                          <div className="text-right text-xs text-gray-400">
                            {new Date(entry.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* S8 New Entry Modal */}
              {showS8Form && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-red-800 flex items-center gap-2">
                        <Lock className="w-5 h-5" /> New S8 Register Entry
                      </h2>
                      <button onClick={() => setShowS8Form(false)} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Drug Name *</label>
                        <input type="text" value={s8FormData.drugName} onChange={(e) => setS8FormData(p => ({ ...p, drugName: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" placeholder="e.g. Morphine" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Strength</label>
                        <input type="text" value={s8FormData.strength} onChange={(e) => setS8FormData(p => ({ ...p, strength: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" placeholder="e.g. 10mg" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type *</label>
                        <select value={s8FormData.transactionType} onChange={(e) => setS8FormData(p => ({ ...p, transactionType: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500">
                          {Object.entries(S8_TRANSACTION_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                        <input type="number" min="0" value={s8FormData.quantity} onChange={(e) => setS8FormData(p => ({ ...p, quantity: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resident Name</label>
                        <input type="text" value={s8FormData.residentName} onChange={(e) => setS8FormData(p => ({ ...p, residentName: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Batch Number</label>
                        <input type="text" value={s8FormData.batchNumber} onChange={(e) => setS8FormData(p => ({ ...p, batchNumber: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Performed By *</label>
                        <input type="text" value={s8FormData.performedByName} onChange={(e) => setS8FormData(p => ({ ...p, performedByName: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" placeholder="Staff name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Witnessed By *</label>
                        <input type="text" value={s8FormData.witnessedByName} onChange={(e) => setS8FormData(p => ({ ...p, witnessedByName: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" placeholder="Witness name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Storage Location</label>
                        <input type="text" value={s8FormData.storageLocation} onChange={(e) => setS8FormData(p => ({ ...p, storageLocation: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" placeholder="e.g. Ward A - Locked Cabinet" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea value={s8FormData.notes} onChange={(e) => setS8FormData(p => ({ ...p, notes: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500" rows={2} />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                      <button onClick={() => setShowS8Form(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button onClick={handleS8Submit} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                        <Save className="w-4 h-4" /> Record Transaction
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'adr' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-orange-800 flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                    Adverse Drug Reactions Register
                  </h3>
                  <button
                    onClick={() => setShowAdrForm(true)}
                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Report ADR
                  </button>
                </div>

                {/* ADR stats */}
                {adrStats && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-white p-3 rounded-lg border border-orange-200 text-center">
                      <p className="text-2xl font-bold text-orange-600">{adrStats.total}</p>
                      <p className="text-xs text-gray-600">Total</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-orange-200 text-center">
                      <p className="text-2xl font-bold text-red-600">{adrStats.active}</p>
                      <p className="text-xs text-gray-600">Active</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-orange-200 text-center">
                      <p className="text-2xl font-bold text-yellow-600">{adrStats.monitoring}</p>
                      <p className="text-xs text-gray-600">Monitoring</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-orange-200 text-center">
                      <p className="text-2xl font-bold text-green-600">{adrStats.resolved}</p>
                      <p className="text-xs text-gray-600">Resolved</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-orange-200 text-center">
                      <p className="text-2xl font-bold text-purple-600">{adrStats.tgaSubmitted}</p>
                      <p className="text-xs text-gray-600">TGA Reported</p>
                    </div>
                  </div>
                )}

                {/* ADR records */}
                {adrRecords.length === 0 ? (
                  <div className="bg-white p-8 rounded-lg border border-orange-200 text-center text-gray-500">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-orange-300" />
                    <p className="font-medium">No adverse drug reactions recorded.</p>
                    <p className="text-sm mt-1">Click "Report ADR" to log a reaction.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...adrRecords].sort((a, b) => new Date(b.dateIdentified) - new Date(a.dateIdentified)).map((adr) => {
                      const sevStyle = ADR_SEVERITY_COLOURS[adr.severity] || ADR_SEVERITY_COLOURS[ADR_SEVERITY.MILD];
                      return (
                        <div key={adr.id} className="bg-white p-4 rounded-lg border border-orange-200 hover:shadow-md transition-all duration-300">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-900">{adr.drug}</h4>
                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${sevStyle.bg} ${sevStyle.text}`}>
                                  {ADR_SEVERITY_LABELS[adr.severity]}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                  adr.status === ADR_STATUS.ACTIVE ? 'bg-red-100 text-red-800'
                                    : adr.status === ADR_STATUS.MONITORING ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-green-100 text-green-800'
                                }`}>
                                  {adr.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 mb-1"><strong>Reaction:</strong> {adr.reaction}</p>
                              {adr.residentName && <p className="text-sm text-gray-600">Resident: {adr.residentName}</p>}
                              {adr.reactionDetails && <p className="text-xs text-gray-500 mt-1 italic">{adr.reactionDetails}</p>}
                              <div className="flex flex-wrap gap-1 mt-2">
                                {(adr.actionsTaken || []).map((a) => (
                                  <span key={a} className="px-2 py-0.5 text-xs rounded bg-blue-50 text-blue-700">{ADR_ACTION_LABELS[a] || a}</span>
                                ))}
                              </div>
                              {adr.reportedTo && adr.reportedTo.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1">Reported to: {adr.reportedTo.map(r => ADR_REPORTED_TO_LABELS[r] || r).join(', ')}</p>
                              )}
                              {adr.tgaBlueCardSubmitted && (
                                <p className="text-xs text-purple-600 mt-1 font-medium">TGA Blue Card: {adr.tgaReferenceNumber || 'Submitted'}</p>
                              )}
                            </div>
                            <div className="text-right flex flex-col items-end gap-2">
                              <p className="text-xs text-gray-400">{new Date(adr.dateIdentified).toLocaleDateString()}</p>
                              <div className="flex flex-wrap gap-1">
                                {adr.status !== ADR_STATUS.RESOLVED && (
                                  <button onClick={() => handleAdrResolve(adr.id)} className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200">Resolve</button>
                                )}
                                {adr.status === ADR_STATUS.ACTIVE && (
                                  <button onClick={async () => {
                                    const followUp = await confirm({ title: 'Follow-up Date', message: 'Enter the follow-up date for monitoring.', inputPlaceholder: 'YYYY-MM-DD', confirmLabel: 'Set Date', variant: 'warning' });
                                    if (followUp) {
                                      const r = await AdverseDrugReactionService.markMonitoring(adr.id, currentUser?.id || 'system', followUp);
                                      if (r.success) { loadAdrData(); showNotification('ADR set to monitoring', 'success'); }
                                    }
                                  }} className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200">Monitor</button>
                                )}
                                {!adr.tgaBlueCardSubmitted && (
                                  <button onClick={async () => {
                                    const refNum = await confirm({ title: 'TGA Submission', message: 'Enter the TGA Reference Number.', inputPlaceholder: 'TGA Reference Number', confirmLabel: 'Submit', variant: 'default' });
                                    if (refNum) {
                                      const r = await AdverseDrugReactionService.recordTGASubmission(adr.id, refNum, currentUser?.id || 'system');
                                      if (r.success) { loadAdrData(); showNotification('TGA submission recorded', 'success'); }
                                    }
                                  }} className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200">TGA</button>
                                )}
                                <button onClick={() => {
                                  setAdrFormData({
                                    residentId: adr.residentId, residentName: adr.residentName,
                                    drug: adr.drug, drugGenericName: adr.drugGenericName || '', drugDose: adr.drugDose || '',
                                    reaction: adr.reaction, reactionDetails: adr.reactionDetails || '',
                                    severity: adr.severity, actionsTaken: adr.actionsTaken || [],
                                    actionNotes: adr.actionNotes || '', reportedTo: adr.reportedTo || [],
                                    dateIdentified: adr.dateIdentified?.split('T')[0] || '',
                                  });
                                  setShowAdrForm(true);
                                }} className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">Edit</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ADR Form Modal */}
              {showAdrForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-orange-800 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" /> Report Adverse Drug Reaction
                      </h2>
                      <button onClick={() => setShowAdrForm(false)} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resident Name *</label>
                        <input type="text" value={adrFormData.residentName} onChange={(e) => setAdrFormData(p => ({ ...p, residentName: e.target.value, residentId: e.target.value.toLowerCase().replace(/\s/g, '_') }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Drug Name *</label>
                        <input type="text" value={adrFormData.drug} onChange={(e) => setAdrFormData(p => ({ ...p, drug: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Drug Dose</label>
                        <input type="text" value={adrFormData.drugDose} onChange={(e) => setAdrFormData(p => ({ ...p, drugDose: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" placeholder="e.g. 10mg" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Severity *</label>
                        <select value={adrFormData.severity} onChange={(e) => setAdrFormData(p => ({ ...p, severity: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500">
                          {Object.entries(ADR_SEVERITY_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reaction Description *</label>
                        <input type="text" value={adrFormData.reaction} onChange={(e) => setAdrFormData(p => ({ ...p, reaction: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" placeholder="e.g. Rash, Nausea, Anaphylaxis" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Detailed Description</label>
                        <textarea value={adrFormData.reactionDetails} onChange={(e) => setAdrFormData(p => ({ ...p, reactionDetails: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" rows={2} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Identified *</label>
                        <input type="date" value={adrFormData.dateIdentified} onChange={(e) => setAdrFormData(p => ({ ...p, dateIdentified: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Actions Taken</label>
                        <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                          {Object.entries(ADR_ACTION_LABELS).map(([k, v]) => (
                            <label key={k} className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={adrFormData.actionsTaken.includes(k)}
                                onChange={(e) => {
                                  setAdrFormData(p => ({
                                    ...p,
                                    actionsTaken: e.target.checked
                                      ? [...p.actionsTaken, k]
                                      : p.actionsTaken.filter(a => a !== k),
                                  }));
                                }}
                                className="rounded text-orange-600"
                              />
                              {v}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reported To</label>
                        <div className="flex flex-wrap gap-3">
                          {Object.entries(ADR_REPORTED_TO_LABELS).map(([k, v]) => (
                            <label key={k} className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={adrFormData.reportedTo.includes(k)}
                                onChange={(e) => {
                                  setAdrFormData(p => ({
                                    ...p,
                                    reportedTo: e.target.checked
                                      ? [...p.reportedTo, k]
                                      : p.reportedTo.filter(r => r !== k),
                                  }));
                                }}
                                className="rounded text-orange-600"
                              />
                              {v}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Action Notes</label>
                        <textarea value={adrFormData.actionNotes} onChange={(e) => setAdrFormData(p => ({ ...p, actionNotes: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" rows={2} />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                      <button onClick={() => setShowAdrForm(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button onClick={handleAdrSubmit} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2">
                        <Save className="w-4 h-4" /> Submit ADR Report
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-6">
              {/* Enhanced AI Insights Dashboard */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-100 p-8 rounded-2xl border border-purple-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-purple-800 mb-2 flex items-center gap-3">
                      <BrainIcon className="w-6 h-6 text-purple-600" />
                      AI-Powered Medication Insights
                    </h3>
                    <p className="text-purple-600">Advanced analytics and predictive recommendations</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">AI Active</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">{aiInsightsData.adherencePatterns.overallScore}%</div>
                        <div className="text-sm text-gray-600">Adherence Score</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Target</span>
                        <span className="text-green-600">95%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{width: `${aiInsightsData.adherencePatterns.overallScore}%`}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl">
                        <AlertTriangle className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-red-600">{aiInsightsData.riskAssessment.highRisk}</div>
                        <div className="text-sm text-gray-600">High Risk</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Total Residents</span>
                        <span className="text-red-600">{aiInsightsData.riskAssessment.totalResidents}</span>
                      </div>
                      <div className="text-xs text-gray-500">Requires immediate attention</div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                        <Cpu className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-blue-600">{aiInsightsData.predictiveAnalytics.medicationErrors.predicted}</div>
                        <div className="text-sm text-gray-600">Predicted Errors</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Confidence</span>
                        <span className="text-blue-600">{aiInsightsData.predictiveAnalytics.medicationErrors.confidence}%</span>
                      </div>
                      <div className="text-xs text-gray-500">Next 7 days</div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-purple-600">{aiInsightsData.recommendations.length}</div>
                        <div className="text-sm text-gray-600">AI Recommendations</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Critical</span>
                        <span className="text-purple-600">3</span>
                      </div>
                      <div className="text-xs text-gray-500">Requires action</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Recommendations and Risk Assessment */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-800">AI Recommendations</h4>
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                        {aiInsightsData.recommendations.length} Active
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    {aiInsightsData.recommendations.map((recommendation) => (
                      <div key={recommendation.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="font-semibold text-sm">{recommendation.title}</h5>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                recommendation.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                                recommendation.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                                recommendation.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {recommendation.priority}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">{recommendation.description}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Target className="w-3 h-3" />
                                {recommendation.impact}
                              </span>
                              <span className="flex items-center gap-1">
                                <BrainIcon className="w-3 h-3" />
                                {recommendation.aiConfidence}% confidence
                              </span>
                            </div>
                            <div className="mt-2">
                              <span className="text-xs text-blue-600">Residents: {recommendation.residents.join(', ')}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-800">Risk Assessment</h4>
                      <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                        {aiInsightsData.riskAssessment.highRisk} High Risk
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{aiInsightsData.riskAssessment.highRisk}</div>
                        <div className="text-xs text-gray-600">High Risk</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-600">{aiInsightsData.riskAssessment.mediumRisk}</div>
                        <div className="text-xs text-gray-600">Medium Risk</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{aiInsightsData.riskAssessment.lowRisk}</div>
                        <div className="text-xs text-gray-600">Low Risk</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h5 className="font-semibold text-sm text-gray-800">Risk Factors</h5>
                      {aiInsightsData.riskAssessment.riskFactors.map((factor, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${
                              factor.risk === 'High' ? 'bg-red-500' :
                              factor.risk === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                            }`}></div>
                            <span className="text-sm font-medium">{factor.factor}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{factor.count} residents</span>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              factor.risk === 'High' ? 'bg-red-100 text-red-800' :
                              factor.risk === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {factor.risk}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Predictive Analytics */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800">Predictive Analytics</h4>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{aiInsightsData.predictiveAnalytics.fallRisk.score}</div>
                      <div className="text-sm text-gray-600 mb-1">Fall Risk Score</div>
                      <div className="text-xs text-green-600">↓ {aiInsightsData.predictiveAnalytics.fallRisk.trend}</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{aiInsightsData.predictiveAnalytics.medicationErrors.predicted}</div>
                      <div className="text-sm text-gray-600 mb-1">Predicted Errors</div>
                      <div className="text-xs text-gray-500">{aiInsightsData.predictiveAnalytics.medicationErrors.timeframe}</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{aiInsightsData.predictiveAnalytics.hospitalReadmission.probability}%</div>
                      <div className="text-sm text-gray-600 mb-1">Readmission Risk</div>
                      <div className="text-xs text-green-600">{aiInsightsData.predictiveAnalytics.hospitalReadmission.risk} risk</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{aiInsightsData.predictiveAnalytics.adverseEvents.predicted}</div>
                      <div className="text-sm text-gray-600 mb-1">Adverse Events</div>
                      <div className="text-xs text-gray-500">{aiInsightsData.predictiveAnalytics.adverseEvents.type}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Active Medication Orders from MedicationService */}
              {medicationOrders.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><Pill className="w-5 h-5 text-blue-600" /> Active Medication Orders ({medicationOrders.filter(o => o.status === 'active').length})</h4>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                    {medicationOrders.filter(o => o.status === 'active').slice(0, 10).map((order) => (
                      <div key={order.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{order.drugName} {order.strength || ''}</p>
                          <p className="text-sm text-gray-600">{order.dose} • {order.route} • {order.frequency}</p>
                          <p className="text-xs text-gray-400">Resident {order.residentId} • {order.orderType}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleViewDetails(order.id)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><Edit3 className="w-4 h-4" /></button>
                          <button onClick={() => handleHoldOrder(order.id, 'On hold')} className="p-2 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg" title="Hold"><Pause className="w-4 h-4" /></button>
                          <button onClick={() => handleCeaseOrder(order.id, 'Discontinued')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Discontinue"><XCircle className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-800">Today's Medication Rounds</h3>
                  </div>
                  <div className="space-y-4">
                    {filteredRounds.slice(0, 3).map((round) => (
                      <div key={round.id} className="bg-white p-4 rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">{round.residentName || 'Unknown Resident'}</h4>
                            <p className="text-sm text-gray-600 mb-2">{round.room || 'Room Unknown'} • {round.medicationsDue || 0} medications</p>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                              <span className="text-xs text-blue-600 font-medium">In Progress</span>
                            </div>
                          </div>
                          <span className="px-3 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 font-medium border border-yellow-200">Pending</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-green-800">Recent Administrations</h3>
                  </div>
                  <div className="space-y-4">
                    {filteredRounds.slice(0, 3).map((round) => (
                      <div key={round.id} className="bg-white p-4 rounded-xl shadow-sm border border-green-100 hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">{round.residentName || 'Unknown Resident'}</h4>
                            <p className="text-sm text-gray-600 mb-2">{round.medications?.[0]?.name || 'Medication details'}</p>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-xs text-green-600 font-medium">Completed</span>
                            </div>
                          </div>
                          <span className="px-3 py-1 text-xs rounded-full bg-green-100 text-green-800 font-medium border border-green-200">Administered</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rounds' && (
            <div className="space-y-6">
              {filteredRounds.length > 0 ? filteredRounds.map((round, index) => (
                <div 
                  key={round.id} 
                  className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.01]"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: 'fadeInUp 0.6s ease-out forwards'
                  }}
                >
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                          {round.avatar || '👵'}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{round.residentName || 'Unknown Resident'}</h3>
                          <p className="text-sm text-gray-600 mb-2">{round.room || 'Room Unknown'} • {round.medicationsDue || 0} medications due</p>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            <span className="text-xs text-blue-600 font-medium">Round in Progress</span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(round.medications || []).map((med, medIndex) => (
                          <div key={medIndex} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-300">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="text-2xl">{med.icon}</span>
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900">{med.name || 'Medication'}</p>
                                <p className="text-sm text-gray-600">{med.instructions || 'No instructions'}</p>
                              </div>
                              <span className={`px-3 py-1 text-xs rounded-full font-medium ${getStatusColor(med.status || 'pending')}`}>
                                {med.status || 'pending'}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <button 
                                onClick={() => handleAdministerMedication(med.name, round.residentName)}
                                disabled={loading}
                                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                              >
                                {loading ? 'Administering...' : 'Administer'}
                              </button>
                              <button 
                                onClick={() => handleViewDetails(med.name)}
                                className="px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button 
                        onClick={() => handleViewDetails(round.id)}
                        className="p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-300"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleCompleteRound(round.id)}
                        disabled={loading}
                        className={`p-3 rounded-xl transition-all duration-300 ${loading ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-green-600 hover:bg-green-50'}`}
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Medication Rounds</h3>
                  <p className="text-gray-600 mb-4">No medication rounds found for the current search criteria.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button 
                      onClick={handleStartNewRound}
                      disabled={loading}
                      className={`px-4 py-2 rounded-lg transition-colors ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                    >
                      {loading ? 'Starting...' : 'Start New Round'}
                    </button>
                    <button 
                      onClick={() => setShowQRScanner(true)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      <QrCode className="w-4 h-4" />
                      Scan QR Code
                    </button>
                    <button 
                      onClick={() => setShowBarcodeScanner(true)}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Package className="w-4 h-4" />
                      Scan Barcode
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {medicationSchedule.map((schedule, index) => (
                  <div 
                    key={schedule.id} 
                    className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{schedule.round}</h3>
                        <p className="text-sm text-gray-600 font-medium">{schedule.time}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                        schedule.status === 'In Progress' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                        schedule.status === 'Scheduled' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                        'bg-green-100 text-green-800 border border-green-200'
                      }`}>
                        {schedule.status}
                      </span>
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600 font-medium">Residents:</span>
                          <span className="font-bold text-gray-900 ml-1">{schedule.residents}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 font-medium">Duration:</span>
                          <span className="font-bold text-blue-600 ml-1">{schedule.duration}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 font-medium">Completed:</span>
                          <span className="font-bold text-green-600 ml-1">{schedule.completed}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 font-medium">Efficiency:</span>
                          <span className="font-bold text-purple-600 ml-1">{schedule.efficiency}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 font-medium">Remaining:</span>
                          <span className="font-bold text-orange-600 ml-1">{schedule.remaining}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 font-medium">Critical Meds:</span>
                          <span className="font-bold text-red-600 ml-1">{schedule.criticalMeds}</span>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-3 mt-4">
                        <p className="text-xs text-gray-600 mb-1"><strong>Nurse:</strong> {schedule.nurse}</p>
                        <p className="text-xs text-gray-500 mb-2">{schedule.notes}</p>
                        <div className="flex flex-wrap gap-1">
                          {schedule.medications.map((med, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                              {med}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                    <FileText className="w-6 h-6 text-blue-600" />
                    Medication Administration History
                  </h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {medicationHistory.map((record, index) => (
                    <div 
                      key={record.id} 
                      className="px-6 py-6 hover:bg-gray-50 transition-all duration-300"
                      style={{
                        animationDelay: `${index * 50}ms`,
                        animation: 'fadeInUp 0.4s ease-out forwards'
                      }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                              <Pill className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-gray-900">{record.residentName}</h4>
                              <p className="text-sm text-gray-600 font-medium">{record.medication}</p>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">{record.date}</p>
                          <p className="text-xs text-gray-600 font-medium">{record.time}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-6 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700">Action:</span>
                            <span className="text-gray-900">{record.action}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700">Nurse:</span>
                            <span className="text-gray-900">{record.nurse}</span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                          record.status === 'completed' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        }`}>
                          {record.status}
                        </span>
                      </div>
                      {record.notes && (
                        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-4 border border-gray-200">
                          <p className="text-sm text-gray-700">
                            <span className="font-semibold text-gray-900">Notes:</span> {record.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Enhanced Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button 
          onClick={() => setShowEPrescribe(true)}
          className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 shadow-lg border border-blue-200 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] w-full text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-blue-900">New Medication</h3>
              <p className="text-sm text-blue-700">Add medication to resident</p>
            </div>
          </div>
        </button>
        
        <button 
          onClick={() => {
            showNotification('Opening medication schedule review', 'info');
            setActiveTab('schedule');
          }}
          className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 shadow-lg border border-green-200 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] w-full text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-green-900">Schedule Review</h3>
              <p className="text-sm text-green-700">Review medication schedules</p>
            </div>
          </div>
        </button>
        
        <button 
          onClick={() => {
            showNotification('Opening medication alerts', 'info');
            setActiveTab('alerts');
          }}
          className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 shadow-lg border border-purple-200 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] w-full text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-purple-900">Alerts</h3>
              <p className="text-sm text-purple-700">View medication alerts</p>
            </div>
          </div>
        </button>
      </div>

      {/* Modals */}
      {showQRScanner && (
        <QRCodeScanner
          onScan={handleQRScan}
          onClose={() => setShowQRScanner(false)}
          isOpen={showQRScanner}
        />
      )}

      {showBarcodeScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowBarcodeScanner(false)}
          isOpen={showBarcodeScanner}
        />
      )}


      {showStockManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Stock Management</h2>
              <button 
                onClick={() => setShowStockManagement(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <StockManagement onClose={() => setShowStockManagement(false)} />
          </div>
        </div>
      )}

      {showBulkOrdering && (
        <BulkOrderingWizard
          onClose={() => setShowBulkOrdering(false)}
        />
      )}

      {showMyHealthRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">My Health Record</h2>
              <button 
                onClick={() => setShowMyHealthRecord(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <MyHealthRecord onClose={() => setShowMyHealthRecord(false)} />
          </div>
        </div>
      )}

      {showPBSEligibility && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">PBS Eligibility</h2>
              <button 
                onClick={() => setShowPBSEligibility(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <PBSEligibility onClose={() => setShowPBSEligibility(false)} />
          </div>
        </div>
      )}

      {showeNRMCCompliance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">eNRMC Compliance</h2>
              <button 
                onClick={() => setShoweNRMCCompliance(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <ENRMCCompliance onClose={() => setShoweNRMCCompliance(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Medication;

